import { useEffect, useLayoutEffect, useMemo, useRef, useState, type TouchEvent } from 'react';
import { ArrowLeft, BookOpen, House, LoaderCircle, RotateCcw, Undo2 } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { AppTopHeader } from '@/components/AppTopHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SpeakButton } from '@/components/SpeakButton';
import { HighlightedSelections, replaceSelectionsWithCloze } from '@/components/HighlightedSelections';
import type { SelectionRange } from '@/generated/api-client';
import { apiClient, pronunciationUrl, type TrainingCard, type TrainingRating } from '@/services';
import { useDueTrainingCount } from '@/contexts/DueTrainingCountContext';

import { TrainingModePicker, isTrainingMode, trainingModes, type TrainingMode } from './TrainingModePicker';
import { TrainingNewsQuote, useTrainingNewsQuotes } from './TrainingNewsQuote';

type TrainingExample = {
  text: string;
  translation: string;
};

type TrainingViewCard = {
  id: string;
  phrase: string;
  sound?: string;
  context: string;
  clozeContext: string;
  selections: SelectionRange[];
  translation: string;
  definition: string;
  examples: TrainingExample[];
  intervalPreviews: Record<string, number>;
};

type TrainingRestoreState = {
  selectedMode: TrainingMode;
  cardId: string;
  revealed: true;
};

const getTrainingRestoreState = (value: unknown): TrainingRestoreState | null => {
  if (!value || typeof value !== 'object') return null;

  const candidate = value as Partial<TrainingRestoreState>;
  if (!isTrainingMode(candidate.selectedMode) || typeof candidate.cardId !== 'string') {
    return null;
  }

  return {
    selectedMode: candidate.selectedMode,
    cardId: candidate.cardId,
    revealed: true,
  };
};

const shouldShowTrainingModePicker = (value: unknown) =>
  Boolean(
    value &&
    typeof value === 'object' &&
    (value as { showTrainingModePicker?: unknown }).showTrainingModePicker === true
  );

const defaultRatingOptions: TrainingRating[] = ['Forgot', 'Hard', 'Easy'];

const getMeaning = (card: TrainingCard) => card.meaning;

const firstNonBlank = (...values: Array<string | undefined>) =>
  values.find((value) => value?.trim())?.trim();

const getSourceContext = (textInput?: string, phrase?: string) => {
  const input = textInput?.trim() ?? '';
  const target = phrase?.trim() ?? '';

  if (!input || input.toLocaleLowerCase() === target.toLocaleLowerCase()) return '';

  return input;
};

const getClozeContext = (
  textInput: string | undefined,
  selections: SelectionRange[],
  phrase: string,
) => {
  const input = textInput ?? '';
  if (!getSourceContext(input, phrase)) return '';

  const selectedCloze = replaceSelectionsWithCloze(input, selections);
  if (selectedCloze !== input) return selectedCloze.trim();

  const phraseStart = input.toLocaleLowerCase().indexOf(phrase.toLocaleLowerCase());
  if (phraseStart < 0) return '';

  return `${input.slice(0, phraseStart)}______${input.slice(phraseStart + phrase.length)}`.trim();
};

const getDefinition = (meaning?: TrainingCard['meaning']): string =>
  meaning?.definition?.trim() ?? '';

const getExamples = (meaning?: TrainingCard['meaning']): TrainingExample[] =>
  (meaning?.examples ?? [])
    .map((example) => ({
      text: example.text ?? '',
      translation: example.translation ?? '',
    }))
    .filter((example) => example.text.trim() || example.translation.trim());

const normalizeTrainingCard = (card: TrainingCard): TrainingViewCard | null => {
  const id = card.cardId;
  if (!id) return null;

  const meaning = getMeaning(card);
  const phrase = firstNonBlank(
    meaning?.displayedText,
    meaning?.canonical,
    meaning?.origin?.text
  ) ?? 'Untitled';
  const translations = (meaning?.translations ?? [])
    .filter((translation) => translation.trim());
  const selections = meaning?.origin?.selections ?? [];

  const training = card.training;
  const intervalPreviews: Record<string, number> = {};
  for (const preview of training?.intervalPreviews ?? []) {
    if (preview.rating) {
      intervalPreviews[preview.rating.toLowerCase()] = preview.intervalHours;
    }
  }

  return {
    id,
    phrase,
    sound: meaning?.sound ?? pronunciationUrl(id),
    context: getSourceContext(meaning?.origin?.text, phrase),
    clozeContext: getClozeContext(meaning?.origin?.text, selections, phrase),
    selections,
    translation: translations.join(' / '),
    definition: getDefinition(meaning),
    examples: getExamples(meaning),
    intervalPreviews,
  };
};

const normalizeRatingOptions = (options?: TrainingRating[]) =>
  options?.length ? options : defaultRatingOptions;

// Phrased for what the reviewer will notice ("today again") rather than the raw
// offset ("+0 hours") — matches the design bundle's rating-button sublabels.
const formatInterval = (hours: number | undefined, rating: TrainingRating) => {
  if (hours === undefined) return '';
  if (hours <= 0) return rating.toLowerCase() === 'forgot' ? 'today again' : 'now';
  if (hours < 24) {
    const rounded = Math.round(hours);
    return `in ${rounded} ${rounded === 1 ? 'hour' : 'hours'}`;
  }

  const days = Math.round(hours / 24);
  return `in ${days} ${days === 1 ? 'day' : 'days'}`;
};

// Warm pastel-per-rating palette from the design bundle, kept local to this
// component instead of the shared grade-* tokens (those are bright/saturated).
const gradeButtonStyles: Record<string, { border: string; bg: string; text: string; sub: string }> = {
  forgot: { border: '#e8cfc8', bg: '#fdf7f5', text: '#a8402a', sub: '#8a6157' },
  hard: { border: '#ecd9bd', bg: '#fdf9f2', text: '#9a6413', sub: '#78654a' },
  // Legacy rating, no longer a selectable button, kept here in case it's referenced anywhere else.
  medium: { border: '#ecd9bd', bg: '#fdf9f2', text: '#9a6413', sub: '#78654a' },
  easy: { border: '#2f5c4e', bg: '#2f5c4e', text: '#fff', sub: '#cfe0d8' },
};

const defaultGradeButtonStyle = {
  border: '#e2e8f0',
  bg: '#fff',
  text: '#334155',
  sub: '#94a3b8',
};

// The example sentences are stored as plain text (no selection ranges like the
// front card's cloze), so the target phrase is highlighted by a literal,
// case-insensitive substring match instead of HighlightedSelections.
function renderExampleWithHighlight(text: string, phrase: string) {
  if (!phrase) return text;
  const index = text.toLowerCase().indexOf(phrase.toLowerCase());
  if (index === -1) return text;

  return (
    <>
      {text.slice(0, index)}
      <span className="not-italic font-bold" style={{ backgroundColor: '#fdf0d3' }}>
        {text.slice(index, index + phrase.length)}
      </span>
      {text.slice(index + phrase.length)}
    </>
  );
}

const MIN_TITLE_FONT_SIZE = 20;

// Shrinks a word title only when the trailing speak button doesn't fit at the end of
// the last text line and wraps onto a line by itself, then aligns the button's vertical
// center onto that line. Recall prompts intentionally have no speak button.
function useFitTitleTextSize(text: string, active: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const title = container?.firstElementChild as HTMLElement | null;
    const button = container?.lastElementChild as HTMLElement | null;
    if (!active || !title || !button || title === button) return;

    const resize = () => {
      title.style.fontSize = '';
      const naturalSize = parseFloat(window.getComputedStyle(title).fontSize);
      if (!naturalSize) return;

      const buttonWrappedAlone = () =>
        button.getBoundingClientRect().top >= title.getBoundingClientRect().bottom - 2;

      let size = naturalSize;
      let guard = 0;
      while (buttonWrappedAlone() && size > MIN_TITLE_FONT_SIZE && guard < 40) {
        size -= 1;
        title.style.fontSize = `${size}px`;
        guard += 1;
      }

      // The button is centred by CSS `vertical-align: middle`, which resolves against its
      // parent's x-height — so the container has to match whatever size the loop above
      // settled the title on, or the button drifts off the lowercase band. Only written
      // when it actually differs, otherwise this re-triggers the ResizeObserver forever.
      const fittedSize = window.getComputedStyle(title).fontSize;
      if (container!.style.fontSize !== fittedSize) {
        container!.style.fontSize = fittedSize;
      }
    };

    resize();

    const observer = new ResizeObserver(resize);
    observer.observe(container!);
    return () => observer.disconnect();
  }, [text, active]);

  return containerRef;
}

function CardSpeakButton({ text, sound }: { text: string; sound?: string }) {
  return (
    <span
      className="inline-flex align-middle"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      onTouchMove={(event) => event.stopPropagation()}
      onTouchEnd={(event) => event.stopPropagation()}
      onTouchCancel={(event) => event.stopPropagation()}
    >
      <SpeakButton
        text={text}
        sound={sound}
        variant="ghost"
        size="icon"
        className="rounded-full text-[#6b6659] hover:bg-[#f1eee8] hover:text-[#26231d]"
      />
    </span>
  );
}

function OpenFullCardButton({
  cardId,
  returnState,
}: {
  cardId: string;
  returnState: TrainingRestoreState;
}) {
  const navigate = useNavigate();

  const openFullCard = () => {
    // Put the training snapshot on the current history entry before opening the
    // detail route. Both the in-app back button and the browser back action can
    // then restore the same card side instead of mounting a fresh mode picker.
    navigate('/training', { replace: true, state: returnState });
    navigate(`/meaning/card/${cardId}`, {
      state: { returnTo: '/training', useHistoryBack: true },
    });
  };

  return (
    <span
      className="inline-flex align-middle"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      onTouchMove={(event) => event.stopPropagation()}
      onTouchEnd={(event) => event.stopPropagation()}
      onTouchCancel={(event) => event.stopPropagation()}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Open full card"
        title="Open full card"
        onClick={openFullCard}
        className="rounded-full text-[#6b6659] hover:bg-[#f1eee8] hover:text-[#26231d]"
      >
        <BookOpen className="size-4" />
      </Button>
    </span>
  );
}

function FlipBackButton({ onFlip, disabled }: { onFlip: () => void; disabled: boolean }) {
  return (
    <span
      className="inline-flex align-middle"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      onTouchMove={(event) => event.stopPropagation()}
      onTouchEnd={(event) => event.stopPropagation()}
      onTouchCancel={(event) => event.stopPropagation()}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        disabled={disabled}
        aria-label="Flip card back to the front"
        title="Flip card back"
        onClick={onFlip}
        className="rounded-full text-[#6b6659] hover:bg-[#f1eee8] hover:text-[#26231d]"
      >
        <Undo2 className="size-4" />
      </Button>
    </span>
  );
}

export default function TrainingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const restoreState = getTrainingRestoreState(location.state);
  const showTrainingModePicker = shouldShowTrainingModePicker(location.state);
  const [cards, setCards] = useState<TrainingViewCard[]>([]);
  const [ratingOptions, setRatingOptions] = useState<TrainingRating[]>(defaultRatingOptions);
  const [selectedMode, setSelectedMode] = useState<TrainingMode | null>(
    restoreState?.selectedMode ?? null
  );
  const [loading, setLoading] = useState(true);
  const [refilling, setRefilling] = useState(false);
  const { dueCount: remainingDueCount, setDueCount: setRemaining } = useDueTrainingCount();
  const [reviewingRating, setReviewingRating] = useState<TrainingRating | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [revealed, setRevealed] = useState(restoreState?.revealed ?? false);
  const [error, setError] = useState('');
  const [reviewedCount, setReviewedCount] = useState(0);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  // A review is "in progress" from the moment a rating is chosen until the request settles.
  const reviewing = reviewingRating !== null;
  const isTrainingMode = selectedMode !== null;

  const currentCard = useMemo(() => cards[0], [cards]);
  const newsQuotes = useTrainingNewsQuotes(currentCard?.id);
  const hardRating = ratingOptions.find((option) => option.toLowerCase() === 'hard') ?? ratingOptions[0];
  const easyRating = ratingOptions.find((option) => option.toLowerCase() === 'easy') ?? ratingOptions[ratingOptions.length - 1];
  const frontText = currentCard
    ? selectedMode === 'en-ru'
      ? currentCard.phrase
      : selectedMode === 'ru-en'
        ? currentCard.translation || 'No translation is available for this card yet.'
        : currentCard.definition || 'No definition is available for this card yet.'
    : '';
  const backTitleText = currentCard?.phrase ?? '';
  const backText = currentCard?.translation || 'No translation is available for this card yet.';
  const frontTitleRef = useFitTitleTextSize(
    frontText,
    selectedMode === 'en-ru' && !revealed && Boolean(currentCard),
  );
  const backTitleRef = useFitTitleTextSize(backTitleText, revealed && Boolean(currentCard));
  const modeTitle = trainingModes.find((mode) => mode.value === selectedMode)?.title ?? '';

  const loadCards = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.getTrainingCards();
      const normalized = (response.cards ?? [])
        .map(normalizeTrainingCard)
        .filter((card): card is TrainingViewCard => Boolean(card));
      const restoredCardIndex = restoreState
        ? normalized.findIndex((card) => card.id === restoreState.cardId)
        : -1;
      const orderedCards = restoredCardIndex > 0
        ? [
            normalized[restoredCardIndex],
            ...normalized.filter((_, index) => index !== restoredCardIndex),
          ]
        : normalized;
      setCards(orderedCards);
      setRatingOptions(normalizeRatingOptions(response.ratingOptions));
      setRemaining(response.dueCount ?? normalized.length);
    } catch {
      setError('Unable to load training cards.');
    } finally {
      setLoading(false);
    }
  };

  // Quietly fetches the next batch once the in-memory batch is exhausted, so the
  // session continues past the server's per-request cap without a full-screen spinner.
  const refillCards = async () => {
    setRefilling(true);
    try {
      const response = await apiClient.getTrainingCards();
      const normalized = (response.cards ?? [])
        .map(normalizeTrainingCard)
        .filter((card): card is TrainingViewCard => Boolean(card));
      setCards(normalized);
      setRemaining(response.dueCount ?? normalized.length);
    } catch {
      setError('Unable to load training cards.');
    } finally {
      setRefilling(false);
    }
  };

  useEffect(() => {
    loadCards();
  }, []);

  const handleReview = async (rating: TrainingRating | undefined) => {
    if (!currentCard || reviewing) return;
    if (!rating) {
      setError('Unable to save training result.');
      return;
    }

    try {
      setReviewingRating(rating);
      setSubmitting(true);
      setError('');
      await apiClient.reviewTrainingCard(currentCard.id, rating);
      setSubmitting(false);
      setSwipeOffset(0);
      setRemaining((current) => (current !== null ? Math.max(0, current - 1) : current));
      setReviewedCount((count) => count + 1);
      setRevealed(false);
      setReviewingRating(null);
      setCards((current) => {
        const next = current.slice(1);
        if (next.length === 0) {
          void refillCards();
        }
        return next;
      });
    } catch {
      setError('Unable to save training result.');
      setSubmitting(false);
      setReviewingRating(null);
    }
  };

  const handleTouchStart = (event: TouchEvent<HTMLElement>) => {
    if (reviewing || !revealed) return;

    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchMove = (event: TouchEvent<HTMLElement>) => {
    if (!touchStartRef.current || reviewing || !revealed) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      setSwipeOffset(Math.max(-140, Math.min(140, deltaX)));
    }
  };

  const handleTouchEnd = () => {
    if (!touchStartRef.current || reviewing || !revealed) return;

    const shouldReview = Math.abs(swipeOffset) >= 72;
    const rating = swipeOffset > 0 ? easyRating : hardRating;
    touchStartRef.current = null;
    setSwipeOffset(0);

    if (shouldReview) {
      void handleReview(rating);
    }
  };

  const revealCard = () => {
    if (!reviewing) {
      setRevealed(true);
    }
  };

  const hideCard = () => {
    if (reviewing) return;

    setRevealed(false);
    setSwipeOffset(0);
    touchStartRef.current = null;
  };

  const returnToModePicker = () => {
    if (reviewing) return;

    setSelectedMode(null);
    setRevealed(false);
    setSwipeOffset(0);
    setReviewedCount(0);
    touchStartRef.current = null;
    navigate('/training', { replace: true, state: null });
  };

  useEffect(() => {
    if (!showTrainingModePicker) return;

    setSelectedMode(null);
    setRevealed(false);
    setSwipeOffset(0);
    setReviewedCount(0);
    touchStartRef.current = null;
  }, [location.key, showTrainingModePicker]);

  const getGradeButtonStyle = (rating: TrainingRating) =>
    gradeButtonStyles[rating.toLowerCase()] ?? defaultGradeButtonStyle;

  return (
    <div className="min-h-screen bg-[#f6f4ef]">
      {/* Desktop chrome for the redesigned Training screens (design bundle: "18a" / "How should we
          ask you?") — same AppTopHeader as /meaning; mobile gets AppShell's MobileAppTopBar. */}
      <div className="hidden md:block">
        <AppTopHeader />
      </div>
      <main
        className={`mx-auto flex min-h-[calc(100dvh-56px)] w-full flex-col px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] pt-6 text-slate-950 sm:px-6 md:min-h-[calc(100dvh-66px)] ${
          selectedMode === null ? 'max-w-5xl md:max-w-none md:px-0 md:py-0' : 'md:pb-6'
        }`}
      >
        {selectedMode === null && (
          <TrainingModePicker
            dueCount={remainingDueCount ?? cards.length}
            loadingCards={loading && remainingDueCount === null}
            onSelect={setSelectedMode}
          />
        )}

        {isTrainingMode && (
          <>
          {/* Pinned to the page's own corner (design bundle "23a"/"23b") rather than the
              narrower reading column below, so it stays put regardless of card width. */}
          <div className="mb-4">
            <Button
              type="button"
              variant="ghost"
              disabled={reviewing}
              onClick={returnToModePicker}
              className="h-auto gap-2 rounded-none px-0 py-1 text-sm font-semibold text-[#57534a] hover:bg-transparent hover:text-[#26231d]"
            >
              <ArrowLeft className="size-4" aria-hidden="true" />
              Training modes
            </Button>
          </div>
          <div className="mx-auto flex w-full min-h-0 max-w-2xl flex-1 flex-col">
          {error && (
          <div role="alert" className="mb-4 flex flex-col gap-3 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between">
            <span>{error}</span>
            <Button
              type="button"
              variant="outline"
              onClick={() => void loadCards()}
              className="h-8 shrink-0 rounded-full border-red-200 bg-white text-red-700 hover:bg-red-100"
            >
              Try again
            </Button>
          </div>
          )}

          {loading && (
          <section aria-label="Loading training cards" className="flex flex-1 items-center">
            <Card className="w-full rounded-2xl border-white bg-white shadow-[0_22px_70px_rgba(15,23,42,0.12)]">
              <CardContent className="flex min-h-[420px] items-center justify-center p-8">
                <LoaderCircle className="size-7 animate-spin text-slate-500" aria-hidden="true" />
                <span className="sr-only">Loading training cards</span>
              </CardContent>
            </Card>
          </section>
          )}

        {!loading && currentCard && (
          <div className="relative flex min-h-0 flex-1 items-center">
            <section
              aria-label="Training card"
              className="relative w-full min-w-0"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
            >
            {/* Stack effect: two receding edges peeking out behind the card, hinting that more
                cards wait in the queue (design bundle "21e"). Desktop only — there's no headroom
                for peeking edges once the card is full width on mobile. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-[38px] -top-[26px] hidden h-[60px] rounded-2xl border border-[#e2ded5] bg-[#eceae3] md:block"
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-[19px] -top-[13px] hidden h-[60px] rounded-[17px] border border-[#e6e2da] bg-[#f4f2ec] md:block"
            />

            {/* Side panel: queue depth + this-card stats, placed in the page's own side margin
                so the card keeps its current width (design bundle "21e"). Only shown once the
                page is wide enough that it can't collide with the centered card. */}
            <div className="absolute right-full top-1/2 mr-12 hidden w-[300px] -translate-y-1/2 flex-col gap-7 text-right 2xl:flex">
              <div className="flex flex-col gap-1.5">
                <div className="text-xs font-bold uppercase tracking-[0.13em] text-training-muted">
                  Left to review
                </div>
                <div className="font-[family-name:var(--font-display)] text-[40px] font-semibold leading-none text-training-ink">
                  {remainingDueCount ?? cards.length} cards
                </div>
                <div className="text-sm text-[#57534a]">{modeTitle}</div>
              </div>
            </div>

            {revealed && (
              <TrainingNewsQuote
                quotes={newsQuotes}
                className="absolute left-full top-1/2 ml-12 hidden w-[280px] -translate-y-1/2 2xl:flex"
              />
            )}

            <Card
              role={revealed ? undefined : 'button'}
              tabIndex={revealed ? undefined : 0}
              aria-pressed={revealed ? undefined : false}
              onClick={revealed ? undefined : revealCard}
              onKeyDown={(event) => {
                if (revealed) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  revealCard();
                }
              }}
              className={`relative z-10 w-full touch-pan-y gap-0 overflow-hidden rounded-2xl border-white bg-white py-0 shadow-[0_22px_70px_rgba(15,23,42,0.12)] transition-transform duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:transition-none ${revealed ? '' : 'cursor-pointer'}`}
              style={{
                transform: `translateX(${swipeOffset}px) rotate(${swipeOffset / 22}deg)`,
              }}
            >
              {!revealed && (
                <CardHeader className="flex min-h-[420px] items-center justify-center px-6 py-7 sm:px-8">
                  <div className="w-full max-w-full">
                    <div ref={frontTitleRef} className="max-w-full text-center">
                      <CardTitle
                        className={`inline break-words text-training-ink ${
                          selectedMode === 'definition-word'
                            ? 'whitespace-pre-line text-2xl font-semibold leading-8 sm:text-[2rem] sm:leading-10'
                            : 'text-4xl font-bold leading-[1.15] sm:text-5xl'
                        }`}
                      >
                        {frontText}
                      </CardTitle>
                      {selectedMode === 'en-ru' && (
                        <CardSpeakButton text={currentCard.phrase} sound={currentCard.sound} />
                      )}
                    </div>
                    {selectedMode === 'en-ru' && currentCard.context && (
                      <div className="mt-4 border-l-2 border-amber-400 pl-4 text-left text-[15px] italic leading-8 text-slate-600">
                        <HighlightedSelections text={currentCard.context} selections={currentCard.selections} />
                      </div>
                    )}
                    {selectedMode !== 'en-ru' && currentCard.clozeContext && (
                      <div className="mt-5 border-l-2 border-amber-400 pl-4 text-left text-[15px] italic leading-8 text-slate-600">
                        {currentCard.clozeContext}
                      </div>
                    )}
                  </div>
                </CardHeader>
              )}
              {revealed && (
                  <CardContent className="px-6 pb-6 pt-4 sm:px-8 sm:pt-5">
                    <div className="flex items-start gap-3">
                      <div ref={backTitleRef} className="min-w-0 flex-1">
                        <h1
                          className="inline break-words text-[38px] font-bold leading-[1.15]"
                          style={{ fontFamily: 'var(--font-display)', color: '#12141a' }}
                        >
                          {backTitleText}
                        </h1>
                        <CardSpeakButton text={currentCard.phrase} sound={currentCard.sound} />
                      </div>
                      <div className="-mr-2 mt-1.5 flex flex-none items-center gap-1">
                        {selectedMode && (
                          <OpenFullCardButton
                            cardId={currentCard.id}
                            returnState={{
                              selectedMode,
                              cardId: currentCard.id,
                              revealed: true,
                            }}
                          />
                        )}
                        <FlipBackButton onFlip={hideCard} disabled={reviewing} />
                      </div>
                    </div>

                  {(currentCard.context || currentCard.examples[0]?.text) && (
                    <div
                      className="mt-[18px] border-l-4 pl-[15px] text-[17px] italic leading-[1.5]"
                      style={{ fontFamily: 'var(--font-display)', borderColor: '#f0c066', color: '#3d3a33' }}
                    >
                      {currentCard.context ? (
                        <HighlightedSelections text={currentCard.context} selections={currentCard.selections} />
                      ) : (
                        renderExampleWithHighlight(currentCard.examples[0].text, currentCard.phrase)
                      )}
                    </div>
                  )}

                  <div className="mt-[18px] flex flex-col gap-2.5 border-t pt-[18px]" style={{ borderColor: '#f1eee8' }}>
                    <p
                      className="whitespace-pre-line break-words text-[19px] font-bold"
                      style={{ color: '#1e1c18' }}
                    >
                      {backText}
                    </p>
                    {currentCard.definition && (
                      <p
                        className="whitespace-pre-line break-words text-[16px] leading-[1.55]"
                        style={{ color: '#3d3a33' }}
                      >
                        {currentCard.definition}
                      </p>
                    )}
                  </div>

                  <div className="mt-6 grid grid-cols-3 gap-2.5">
                    {ratingOptions.map((rating) => {
                      const style = getGradeButtonStyle(rating);
                      const interval = formatInterval(currentCard.intervalPreviews[rating.toLowerCase()], rating);
                      const isSelected = reviewingRating === rating;
                      const isSubmitting = submitting && isSelected;

                      return (
                        <button
                          key={rating}
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleReview(rating);
                          }}
                          disabled={reviewing}
                          aria-label={`Rate card ${rating}`}
                          className="flex h-[66px] flex-col items-center justify-center gap-0.5 rounded-[13px] border-[1.5px] px-0.5 transition-[opacity,box-shadow,transform] duration-[350ms] ease-out"
                          style={{
                            borderColor: style.border,
                            background: style.bg,
                            opacity: reviewing && !isSelected ? 0.32 : 1,
                            boxShadow: isSelected ? '0 0 0 3px rgba(47,92,78,.18)' : 'none',
                            transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                          }}
                        >
                          <span
                            className="flex items-center gap-1.5 text-[15px] font-extrabold leading-normal"
                            style={{ color: style.text }}
                          >
                            {isSubmitting && <LoaderCircle className="size-3.5 animate-spin" aria-hidden="true" />}
                            {rating}
                          </span>
                          {interval && (
                            <span
                              className="whitespace-nowrap text-[12.5px] font-medium leading-normal"
                              style={{ color: style.sub }}
                            >
                              {interval}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
            </section>
          </div>
        )}

        {!loading && refilling && !currentCard && (
          <section aria-label="Loading more training cards" className="flex flex-1 items-center justify-center">
            <LoaderCircle className="size-6 animate-spin text-slate-400" aria-hidden="true" />
            <span className="sr-only">Loading more training cards</span>
          </section>
        )}

        {!loading && !refilling && !error && !currentCard && reviewedCount > 0 && (
          <section aria-label="Training complete" className="flex flex-1 flex-col items-center justify-center text-center">
            <img
              src="/training-modes/training-complete.webp"
              alt=""
              aria-hidden="true"
              width={1122}
              height={1402}
              className="h-auto w-full max-w-[260px] sm:max-w-[300px]"
            />
            <h1 className="mt-4 font-[family-name:var(--font-display)] text-[28px] font-semibold leading-[1.2] text-training-ink">
              All done for now!
            </h1>
            <p className="mt-2 max-w-sm text-[15px] leading-[1.55] text-[#6b6659]">
              You've reviewed every due card. New ones will show up here when they're due.
            </p>
            <Link
              to="/meaning"
              className="mt-6 flex h-[42px] items-center gap-2 whitespace-nowrap rounded-full bg-[#0f1115] px-[22px] text-sm font-bold text-white transition hover:bg-[#1e1c18]"
            >
              <House className="size-4" aria-hidden="true" />
              Back to home
            </Link>
          </section>
        )}

        {!loading && !refilling && !currentCard && (reviewedCount === 0 || Boolean(error)) && (
          <section className="flex flex-1 items-center justify-center">
            <div className="w-full rounded-lg border border-dashed border-slate-200 bg-white/70 p-8 text-center">
              <p className="text-lg font-semibold text-slate-800">No cards to train right now.</p>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                Due cards will appear here when their next training time arrives.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={loadCards}
                className="mt-5 h-11 rounded-full border-white bg-white text-slate-800 shadow-[0_10px_30px_rgba(15,23,42,0.07)] hover:bg-slate-50"
              >
                <RotateCcw className="size-4" />
                Refresh
              </Button>
            </div>
          </section>
        )}
          </div>
          </>
        )}
      </main>
    </div>
  );
}
